import { datadogRum } from '@datadog/browser-rum';
import { isLocalEnvironment } from '../environment';

// Informational logging

function logInfoToConsole(message: string, context: Record<string, unknown>) {
  console.log({ message, context });
}

function logInfoToTelemetry(message: string, context: Record<string, unknown>) {
  datadogRum.addAction(message, context);
}

export function logInfo(message: string, context: Record<string, unknown> = {}) {
  if (isLocalEnvironment) {
    logInfoToConsole(message, context);
  } else {
    logInfoToTelemetry(message, context);
  }
}

// Error logging

function logErrorToConsole(error: Error, context: Record<string, unknown>) {
  console.error({
    message: error.message,
    stack: error.stack,
    context
  });
}

function logErrorToTelemetry(error: Error, context: Record<string, unknown>) {
  datadogRum.addError(error, context);
}

export function isErrorWithResponse(err: unknown) {
  return typeof err === 'object' && err !== null && 'response' in err;
}

export function toError(err: unknown) {
  return err instanceof Error ? err : new Error(String(err));
}

type LogErrorOptions = {
  callback?: (error: Error) => void;
  controller?: AbortController;
};

export function logError(
  err: unknown,
  context: Record<string, unknown> = {},
  options: LogErrorOptions = {}
) {
  const error = toError(err);
  const { callback, controller } = options;

  // Log aborts as info instead of errors
  if (controller?.signal?.aborted) {
    logInfo('Request aborted', {
      ...context,
      reason: controller.signal.reason
    });

    return;
  }

  if (isLocalEnvironment) {
    logErrorToConsole(error, { err, ...context });
  } else {
    logErrorToTelemetry(error, { err, ...context });
  }

  if (callback) callback(error);
}

export const generateErrorLogger =
  (location: string, globalContext: Record<string, unknown>, options: LogErrorOptions = {}) =>
  (err: unknown, instanceContext: Record<string, unknown> = {}) =>
    logError(
      err,
      {
        location,
        ...globalContext,
        ...instanceContext,
        url: window.location.href
      },
      options
    );
